import type { JobStatus } from '@/features/jobs/job-status'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { match } from '@/utils/pattern-match-util'

/**
 * The icon drawn for a job status.
 */
export type Glyph =
  | 'circle'
  | 'dashed'
  | 'loader'
  | 'half'
  | 'up'
  | 'alert'
  | 'pen'
  | 'check'
  | 'x'
  | 'pause'
  | 'clock'
  | 'slash'

/**
 * The colour family a job status is drawn in.
 */
export type Tone = 'idle' | 'active' | 'ready' | 'warning' | 'done' | 'muted'

/**
 * The status glyph and tone shown for a job in lists and headings.
 *
 * @param job
 */
export function statusGlyph(
  job: Pick<SearchableJob, 'status' | 'resolution' | 'progressionStep'>,
): [Glyph, Tone] {
  switch (job.status) {
    case 'Closed':
      switch (job.resolution) {
        case 'Cancelled':
          return ['x', 'muted']
        case 'Test':
          return ['slash', 'muted']
        default:
          return ['check', 'done']
      }
    case 'OnHold':
      return ['pause', 'idle']
    case 'UnderConstruction':
    case 'InfoGathering':
    case 'Pending':
      return ['dashed', 'idle']
    case 'Unclaimed':
      return ['circle', 'idle']
    case 'Idle':
      return ['clock', 'idle']
  }

  switch (job.progressionStep) {
    case 'OpenItems':
    case 'ProgressionWaitingOnClient':
      return ['dashed', 'idle']
    case 'DraftInReview':
      return ['half', 'active']
    case 'DraftRejected':
    case 'ExtensionRejected':
      return ['alert', 'warning']
    case 'DraftApproved':
    case 'AuthorizationSigned':
    case 'ReadyToFile':
      return ['up', 'ready']
    case 'AuthorizationRequested':
      return ['pen', 'idle']
    case 'ReturnFiled':
    case 'DeliverableCompleted':
    case 'PreparationCompleted':
    case 'ReviewCompleted':
      return ['check', 'done']
    case 'ExtensionNotStarted':
    case 'ExtensionInProgress':
    case 'ExtensionConfirmed':
    case 'ExtensionAccepted':
      return ['clock', 'muted']
    default:
      return ['loader', 'active']
  }
}

/**
 * The label shown for a job's status.
 *
 * @param status
 */
export function getStatusDisplayText(status: JobStatus) {
  return match(status, {
    UnderConstruction: 'Onboarding',
    InfoGathering: 'Info Gathering',
    Unclaimed: 'Unclaimed',
    Claimed: 'In Progress',
    Closed: 'Closed',
    Idle: 'Idle',
    OnHold: 'On Hold',
    Pending: 'Pending',
  })
}
